import React, { useEffect, useRef, useState, useContext } from "react";
import { LanguageContext } from "../../Languages/LanguageContext";
import { useDispatch } from "react-redux";
import {
  assignPartner,
  fetchAllDeliveryRequests,
  fetchDeliveryPartner,
} from "../../../features/dashboard/newBuilderSlice";
import toast from "react-hot-toast";
import "./AssignPersonModal.css";

const AssignPersonModal = ({ show, onClose, deliveryRequestId }) => {
  const { t } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const tableRef = useRef(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [allPartners, setAllPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  // 🔹 Reset when modal opens
  useEffect(() => {
    if (show) {
      setAllPartners([]);
      setPage(1);
    }
  }, [show]);

  // 🔹 Fetch paginated data in correct order
  useEffect(() => {
    if (!show) return;

    let isActive = true; // cancel flag for async calls

    const loadPartners = async () => {
      try {
        setLoading(true);
        const result = await dispatch(
          fetchDeliveryPartner({ page, limit }),
        ).unwrap();

        if (!isActive) return;

        if (result?.data?.length) {
          setAllPartners((prev) => {
            const existing = new Set(prev.map((p) => p.id));
            const newItems = result.data.filter((p) => !existing.has(p.id));
            // ✅ Append below (no sorting)
            return [...prev, ...newItems];
          });
        }

        setTotalPages(result?.pagination?.total_pages || 1);
      } catch (err) {
        console.error("Error loading partners:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPartners();

    return () => {
      isActive = false;
    };
  }, [page, show, dispatch, limit]);

  // 🔹 Infinite scroll
  useEffect(() => {
    if (!show || !tableRef.current) return;
    const container = tableRef.current;
    let fetching = false;

    const handleScroll = () => {
      if (fetching || loading || page >= totalPages) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        fetching = true;
        setPage((prev) => prev + 1);
        setTimeout(() => (fetching = false), 800);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [loading, show, page, totalPages]);

  // 🔹 Assign partner
  const handleAssign = (partner_id) => {
    if (!deliveryRequestId) {
      toast.error("Missing Delivery Request ID!");
      return;
    }

    dispatch(
      assignPartner({ partner_id, delivery_request_id: deliveryRequestId }),
    )
      .unwrap()
      .then((result) => {
        toast.success(result?.message || "Partner assigned successfully!");
        onClose();
        dispatch(fetchAllDeliveryRequests({ page: 1, limit: 10 }));
      })
      .catch(() => {
        toast.error("Failed to assign partner. Please try again.");
      });
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          zIndex: 1040,
        }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal show responsive-container"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1050,

          maxWidth: "1200px",
          display: "block",
        }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-info text-white">
              <h5 className="modal-title">
                {t?.deliveryrequest || "Assign Delivery Partner"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>

            <div className="modal-body">
              <div
                className="table-responsive"
                ref={tableRef}
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                }}
              >
                <table className="table table-bordered table-hover align-middle text-center text-info">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Created At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPartners.length > 0 ? (
                      allPartners.map((item) => (
                        <tr key={item.id}>
                          <td>{item.name}</td>
                          <td>{item.username}</td>
                          <td>{item.email}</td>
                          <td>{item.phone}</td>
                          <td>{item.created_at}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleAssign(item.id)}
                            >
                              Assign
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-muted text-center">
                          {loading
                            ? "Loading Delivery Persons..."
                            : "No Delivery Partners Found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {loading && allPartners.length > 0 && (
                  <div className="text-center py-2 text-secondary">
                    Loading more...
                  </div>
                )}
                {!loading && page >= totalPages && allPartners.length > 0 && (
                  <div className="text-center py-2 text-muted fst-italic">
                    All partners loaded
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignPersonModal;
